// src/pages/tasks/TaskByEmployee.jsx
import React, { useEffect, useState } from "react";
import api from "../../api";
import styles from "./TaskByEmployee.module.css";

const STATUS_LABEL = {
  pending: { label: "대기", color: "#6c757d" },
  inprogress: { label: "진행중", color: "#0d6efd" },
  done: { label: "완료", color: "#198754" },
};

const STATUS_OPTIONS = [
  { value: "pending", label: "대기" },
  { value: "inprogress", label: "진행중" },
  { value: "done", label: "완료" },
];

// ── 수정 모달 ──────────────────────────────────────────────
function EditModal({ task, onClose, onSave }) {
  const [form, setForm] = useState({
    title: task.title ?? "",
    due_date: task.due_date ? task.due_date.slice(0, 16) : "",
    priority: task.priority ?? 0,
    status: task.status ?? "pending",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const res = await api.patch(`/tasks/${task.id}/update/`, form);
      onSave(res.data);
    } catch (e) {
      alert("수정 실패: " + (e.response?.data?.detail ?? e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalTitle}>업무 수정</div>

        <label className={styles.modalLabel}>업무 제목</label>
        <input
          className={styles.modalInput}
          value={form.title}
          onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
        />

        <label className={styles.modalLabel}>마감일</label>
        <input
          type="datetime-local"
          className={styles.modalInput}
          value={form.due_date}
          onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
        />

        <label className={styles.modalLabel}>중요도</label>
        <div className={styles.starRow}>
          {[1, 2, 3].map(n => (
            <span
              key={n}
              className={styles.starBtn}
              onClick={() => setForm(f => ({ ...f, priority: n }))}
            >
              {n <= form.priority ? "★" : "☆"}
            </span>
          ))}
        </div>

        <label className={styles.modalLabel}>상태</label>
        <select
          className={styles.modalInput}
          value={form.status}
          onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
        >
          {STATUS_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>

        <div style={{ marginTop: 14, padding: 12, background: "#f6f8fa", borderRadius: 8, fontSize: 13, lineHeight: 1.9 }}>
          <div style={{ fontSize: 11, color: "#888", marginBottom: 4 }}>난이도 · 소요시간 (읽기 전용)</div>
          <div>지시 난이도: {task.difficulty != null ? `${task.difficulty} / 10` : "-"}</div>

          <div>예상 소요: {task.est_minutes != null
            ? task.est_minutes < 60
              ? `${task.est_minutes}분`
              : task.est_minutes % 60 === 0
                ? `${Math.floor(task.est_minutes / 60)}시간`
                : `${Math.floor(task.est_minutes / 60)}시간 ${task.est_minutes % 60}분`
            : "-"}</div>
          <div>실제 난이도: {task.actual_difficulty != null ? `${task.actual_difficulty} / 10` : "-"}</div>
          <div>실제 소요: {task.actual_minutes != null
            ? task.actual_minutes < 60
              ? `${task.actual_minutes}분`
              : task.actual_minutes % 60 === 0
                ? `${Math.floor(task.actual_minutes / 60)}시간`
                : `${Math.floor(task.actual_minutes / 60)}시간 ${task.actual_minutes % 60}분`
            : "-"}</div>

        </div>

        <div className={styles.modalActions}>




          <button className={styles.btnCancel} onClick={onClose}>취소</button>
          <button className={styles.btnSave} onClick={handleSubmit} disabled={saving}>
            {saving ? "저장중..." : "저장"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── 모바일 카드 뷰 ──────────────────────────────────────────
function TaskCard({ t, idx, onEdit, onDelete }) {
  const st = STATUS_LABEL[t.status] ?? { label: t.status, color: "#333" };
  const isOverdue = (due, status) =>
    due && status !== "done" && new Date(due) < new Date(new Date().toDateString());

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.cardNo}>#{idx + 1}</span>

        <span className={styles.statusBadge} style={{ background: st.color }}>
          {t.status_display ?? st.label}
        </span>
        {t.comment_count > 0 && t.status !== "done" && (
          <span className={styles.chatBadge}>💬</span>
        )}


      </div>

      <div className={styles.cardTitle}>{t.title}</div>
      <div className={styles.cardMeta}>
        <span className={styles.cardMetaItem}>
          <span className={styles.cardMetaLabel}>지시자</span>
          {t.assigner_name ?? "-"}
        </span>
        <span className={styles.cardMetaItem}>
          <span className={styles.cardMetaLabel}>중요도</span>
          <span className={styles.stars}>
            {"★".repeat(t.priority ?? 0)}{"☆".repeat(3 - (t.priority ?? 0))}
          </span>
        </span>
        <span className={styles.cardMetaItem}>
          <span className={styles.cardMetaLabel}>마감일</span>
          <span className={isOverdue(t.due_date, t.status) ? styles.overdueDate : ""}>
            {t.due_date?.slice(0, 16).replace("T", " ") ?? "-"}
          </span>
        </span>
        <span className={styles.cardMetaItem}>
          <span className={styles.cardMetaLabel}>지시일</span>
          {t.created_at?.slice(0, 10) ?? "-"}
        </span>
      </div>
      <div className={styles.cardActions}>
        <button className={styles.btnEdit} onClick={() => onEdit(t)}>수정</button>
        <button className={styles.btnDelete} onClick={() => onDelete(t.id)}>삭제</button>
      </div>
    </div>
  );
}

// ── 메인 컴포넌트 ──────────────────────────────────────────
export default function TaskByEmployee() {
  const [tasks, setTasks] = useState([]);
  const [empList, setEmpList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [editTask, setEditTask] = useState(null);

  // 모바일에서 직원 선택 후 task 뷰로 전환하는 상태
  const [mobileView, setMobileView] = useState("list"); // "list" | "tasks"

  useEffect(() => {
    api.get("/tasks/all-by-assignee/")
      .then(res => {
        const data = res.data ?? [];
        setTasks(data);

        const seen = new Set();
        const emps = [];
        data.forEach(t => {
          const key = t.assignee;
          if (key && !seen.has(key)) {
            seen.add(key);
            emps.push({
              id: t.assignee,
              name: t.assignee_name,
              position: t.assignee_position ?? "",
              positionRank: t.assignee_position_rank ?? 99,
            });
          }
        });
        emps.sort((a, b) => {
          const rankDiff = a.positionRank - b.positionRank;
          if (rankDiff !== 0) return rankDiff;
          return a.name.localeCompare(b.name, "ko");
        });
        setEmpList(emps);
        if (emps.length > 0) setSelected(emps[0].id);
      })
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  }, []);

  const handleDelete = async (taskId) => {
    if (!window.confirm("이 업무를 삭제하시겠습니까?")) return;
    try {
      await api.delete(`/tasks/${taskId}/delete/`);
      setTasks(prev => prev.filter(t => t.id !== taskId));
    } catch (e) {
      alert("삭제 실패: " + (e.response?.data?.detail ?? e.message));
    }
  };

  const handleSave = (updated) => {
    setTasks(prev => prev.map(t => t.id === updated.id ? { ...t, ...updated } : t));
    setEditTask(null);
  };

  const countFor = (empId, status) =>
    tasks.filter(t =>
      t.assignee === empId && (status === "all" ? true : t.status === status)
    ).length;

  const myTasks = tasks.filter(t => {
    if (t.assignee !== selected) return false;
    return filter === "all" ? true : t.status === filter;
  });

  const isOverdue = (due, status) =>
    due && status !== "done" && new Date(due) < new Date(new Date().toDateString());

  if (loading) return <div className={styles.loading}>불러오는 중...</div>;

  // ── 직원 선택 (모바일: task 뷰로 전환) ──
  const handleSelectEmp = (empId) => {
    setSelected(empId);
    setFilter("all");
    setMobileView("tasks");
  };

  const emp = empList.find(e => e.id === selected);

  return (
    <div className={styles.wrap}>

      {/* 수정 모달 */}
      {editTask && (
        <EditModal
          task={editTask}
          onClose={() => setEditTask(null)}
          onSave={handleSave}
        />
      )}

      {/* ══════════════════════════════════════════
          PC 레이아웃 (flex: sidebar + main)
          모바일에서는 .wrap이 block으로 전환됨
      ══════════════════════════════════════════ */}

      {/* ── 좌측 직원 목록 ── */}
      <aside className={`${styles.sidebar} ${mobileView === "tasks" ? styles.sidebarHiddenMobile : ""}`}>
        <div className={styles.sideTitle}>직원 목록</div>
        {empList.length === 0 && (
          <div className={styles.empty}>업무 데이터 없음</div>
        )}
        {empList.map(emp => {
          const pending = countFor(emp.id, "pending");
          const inProg = countFor(emp.id, "inprogress");
          const total = countFor(emp.id, "all");
          return (
            <button
              key={emp.id}
              type="button"
              className={`${styles.empBtn} ${selected === emp.id ? styles.empBtnActive : ""}`}
              onClick={() => handleSelectEmp(emp.id)}
            >
              <span className={styles.empName}>
                {emp.name}{emp.position ? ` ${emp.position}` : ""}
              </span>


              <span className={styles.empBadges}>
                <span className={styles.badgeTotal}>{total}건</span>
                {countFor(emp.id, "done") > 0 && <span className={styles.badgeGreen}>{countFor(emp.id, "done")} 완료</span>}
                {pending > 0 && <span className={styles.badgeGray}>{pending} 대기</span>}
                {inProg > 0 && <span className={styles.badgeBlue}>{inProg} 진행</span>}
                {tasks.filter(t => t.assignee === emp.id && t.comment_count > 0 && t.status !== "done").length > 0 && (
                  <span className={styles.badgeChat}>
                    💬 {tasks.filter(t => t.assignee === emp.id && t.comment_count > 0 && t.status !== "done").length}
                  </span>
                )}
              </span>


            </button>
          );
        })}
      </aside>

      {/* ── 우측 업무 목록 ── */}
      <main className={`${styles.main} ${mobileView === "list" ? styles.mainHiddenMobile : ""}`}>
        {!selected ? (
          <div className={styles.empty}>직원을 선택하세요.</div>
        ) : (
          <>
            <div className={styles.mainHeader}>
              <div className={styles.empTitle}>
                {/* 모바일 뒤로가기 버튼 */}
                <button
                  className={styles.backBtn}
                  onClick={() => setMobileView("list")}
                  aria-label="목록으로"
                >
                  ← 목록
                </button>
                {emp?.name}
                {emp?.position && (
                  <span className={styles.empSubtitle}>{emp.position}</span>
                )}
              </div>
              <div className={styles.filterRow}>
                {[
                  ["all", "전체"],
                  ["pending", "대기"],
                  ["inprogress", "진행중"],
                  ["done", "완료"],
                ].map(([val, lbl]) => (
                  <button
                    key={val}
                    type="button"
                    className={`${styles.filterBtn} ${filter === val ? styles.filterBtnActive : ""}`}
                    onClick={() => setFilter(val)}
                  >
                    {lbl} <span className={styles.filterCount}>{countFor(selected, val)}</span>
                  </button>
                ))}
              </div>
            </div>

            {myTasks.length === 0 ? (
              <div className={styles.empty}>해당 조건의 업무가 없습니다.</div>
            ) : (
              <>
                {/* PC: 테이블 */}
                <table className={`${styles.table} ${styles.pcOnly}`}>
                  <thead>
                    <tr>
                      <th>No</th>
                      <th>업무 제목</th>
                      <th>지시자</th>
                      <th>중요도</th>
                      <th>마감일</th>
                      <th>지시일</th>
                      <th>상태</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {myTasks.map((t, idx) => {
                      const st = STATUS_LABEL[t.status] ?? { label: t.status, color: "#333" };
                      return (
                        <tr key={t.id}>
                          <td>{idx + 1}</td>
                          <td className={styles.taskTitle}>{t.title}</td>
                          <td>{t.assigner_name}</td>
                          <td className={styles.stars}>
                            {"★".repeat(t.priority ?? 0)}{"☆".repeat(3 - (t.priority ?? 0))}
                          </td>
                          <td className={isOverdue(t.due_date, t.status) ? styles.overdueDate : ""}>
                            {t.due_date?.slice(0, 16).replace("T", " ") ?? "-"}
                          </td>
                          <td>{t.created_at?.slice(0, 10)}</td>

                          <td>
                            <span className={styles.statusBadge} style={{ background: st.color }}>
                              {t.status_display ?? st.label}
                            </span>
                            {t.comment_count > 0 && t.status !== "done" && (
                              <span className={styles.chatBadge}>💬</span>
                            )}
                          </td>


                          <td className={styles.actionCell}>
                            <button className={styles.btnEdit} onClick={() => setEditTask(t)}>수정</button>
                            <button className={styles.btnDelete} onClick={() => handleDelete(t.id)}>삭제</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {/* 모바일: 카드 목록 */}
                <div className={`${styles.cardList} ${styles.mobileOnly}`}>
                  {myTasks.map((t, idx) => (
                    <TaskCard
                      key={t.id}
                      t={t}
                      idx={idx}
                      onEdit={setEditTask}
                      onDelete={handleDelete}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}